/**
 * Familienkonto — Personen-Formular (hinzufügen / bearbeiten)
 *
 * Kopfblock „Profil" (Rolle/Familiengrad/Haushalt/Pflegegrad) über
 * `ProfilHeadSection`. Ist die Rolle ein Pflegeempfänger (Frau/Mann,
 * `is_care_recipient`), werden die vollen Pflege-Sektionen gezeigt
 * (`FormSections`); für Rechnungsempfänger/Hausarzt/Kontaktperson nur die
 * schlanke Sicht (Stammdaten + Adresse + Rolle/Familiengrad/Haushalt).
 *
 * Optionen (profiltyp/familiengrad) kommen LIVE aus `v1ProfileOptions.grouped()`.
 * Speichern ruft entweder POST members (Person hinzufügen) oder PUT profiles
 * (Person bearbeiten).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { getThemeColors } from '../../../lib/theme';
import {
  v1ProfileOptions, type V1ProfileOption,
} from '../../../lib/v1';
import {
  v2Members, isCareRecipientType,
  type FamilyMember, type FamilyMemberInput,
} from '../../../lib/familyAccounts';
import { toStaleWriteError } from '../../../lib/optimisticLock';
import { labelOptions } from '../profile/options';
import { Section, Grid, Field, Text, Select, PhoneInput } from '../profile/controls';
import { ProfilHeadSection, FormSections, selectedRoles, type PbFormState } from '../profile/sections';
import { validateProfile, isRequiredField, missingFieldLabels } from '../profile/validation';
import { freezeSektionenAktiv, inactiveSectionFields, parseSektionenAktiv } from '../profile/sectionToggles';
import {
  MEMBER_EXTRA_PROFILE_FIELDS, MEMBER_EXTRA_USER_FIELDS, pickPayload, pflegegradLevel,
} from '../profile/fields';

interface Props {
  /** Konto-ID — nötig für „Person hinzufügen" (POST members). */
  accountId: number | null;
  /** Bestehende Person zum Bearbeiten, oder null für „neue Person". */
  member: FamilyMember | null;
  dark: boolean;
  onSaved: (m: FamilyMember) => void;
  onCancel?: () => void;
}

/** FamilyMember → flacher Formular-State. */
function memberToForm(m: FamilyMember | null): PbFormState {
  if (!m) return { haushalt: false, is_pflegeempfaenger: false };

  // Zusatzfelder aus der Antwort übernehmen, damit die vollen Sektionen beim
  // Bearbeiten nicht leer erscheinen (und ein Speichern sie nicht leert).
  const rec = m as unknown as Record<string, unknown>;
  const extra: Record<string, unknown> = {};
  for (const k of [...MEMBER_EXTRA_PROFILE_FIELDS, ...MEMBER_EXTRA_USER_FIELDS]) {
    const v = rec[k as string];
    if (v !== undefined && v !== null) extra[k as string] = v;
  }

  // Abgeleitete Sektions-Schalter festschreiben (s. freezeSektionenAktiv).
  return freezeSektionenAktiv({
    ...(extra as Partial<PbFormState>),
    // PHP liefert ein leeres Objekt als [] — normalisieren.
    sektionen_aktiv: parseSektionenAktiv(rec.sektionen_aktiv),
    profiltyp: m.profiltyp ?? undefined,
    profiltypen: Array.isArray(m.profiltypen) ? m.profiltypen : (m.profiltyp ? [m.profiltyp] : []),
    // Backward compat: server should return string[], but handle legacy string.
    familiengrad: Array.isArray(m.familiengrad)
      ? m.familiengrad
      : m.familiengrad ? [(m.familiengrad as unknown as string)] : [],
    is_pflegeempfaenger: m.is_pflegeempfaenger ?? false,
    haushalt: m.haushalt,
    pflegegrad_level: m.pflegegrad == null ? '' : String(m.pflegegrad),
    // Die Pflege-Sektion arbeitet mit der profile_options-ID (Level + 4) und
    // ist dort Pflichtfeld — ohne diese Hydrierung stünde sie beim Bearbeiten
    // leer und ein Speichern wäre ohne Neuauswahl nicht möglich.
    pflegegrad: m.pflegegrad == null ? undefined : String(Number(m.pflegegrad) + 4),
    anrede: m.anrede ?? undefined,
    titel: m.titel ?? undefined,
    vorname: m.vorname ?? undefined,
    nachname: m.nachname ?? undefined,
    email: m.email ?? undefined,
    mobile: m.mobile ?? undefined,
    telefon: m.telefon ?? undefined,
    strasse: m.address?.strasse ?? undefined,
    hausnummer: m.address?.hausnummer ?? undefined,
    plz: m.address?.plz ?? undefined,
    ort: m.address?.ort ?? undefined,
    land: m.address?.land ?? undefined,
  });
}

/**
 * Formular-State → API-Payload.
 *
 * Für einen Pflegeempfänger rendert dieses Formular dieselben Sektionen wie
 * der Profildaten-Tab. Entsprechend müssen auch dieselben Felder mit — vorher
 * gingen hier ~60 ausgefüllte Felder (Pflege, Versicherung, Wunsch-BK,
 * Freitexte, Bank, Status) still verloren und waren nach dem Neuladen weg.
 */
function formToPayload(f: PbFormState, careRecipient: boolean): FamilyMemberInput {
  const pg = pflegegradLevel(f);
  // Felder deaktivierter Sektionen (02–07) nicht mitschicken — sie bleiben
  // unverändert, bis die Sektion wieder eingeschaltet wird.
  const inactive = inactiveSectionFields(f);
  const extraProfile = MEMBER_EXTRA_PROFILE_FIELDS.filter(k => !inactive.has(k));
  const extra = careRecipient
    ? {
        ...pickPayload(f, extraProfile),
        ...pickPayload(f, MEMBER_EXTRA_USER_FIELDS),
      }
    : {};

  return {
    ...extra,
    profiltyp: String(f.profiltyp ?? ''),
    profiltypen: selectedRoles(f),
    familiengrad: f.familiengrad ?? [],
    is_pflegeempfaenger: f.is_pflegeempfaenger ?? false,
    haushalt: Boolean(f.haushalt),
    pflegegrad: pg,
    anrede: f.anrede ?? null,
    titel: f.titel ?? null,
    vorname: f.vorname ?? null,
    nachname: f.nachname ?? null,
    email: f.email ?? null,
    mobile: f.mobile ?? null,
    telefon: f.telefon ?? null,
    strasse: f.strasse ?? null,
    hausnummer: f.hausnummer ?? null,
    plz: f.plz ?? null,
    ort: f.ort ?? null,
    land: f.land ?? null,
  };
}

export function FamilyMemberForm({ accountId, member, dark, onSaved, onCancel }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [f, setF] = useState<PbFormState>(() => memberToForm(member));
  const [opts, setOpts] = useState<Record<string, V1ProfileOption[]> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Array<keyof PbFormState>>([]);
  // Optimistisches Locking: der Stand, den dieses Formular geladen hat. Wird
  // beim Speichern mitgeschickt; die API lehnt mit 409 ab, wenn jemand anderes
  // die Person inzwischen gespeichert hat (siehe src/lib/optimisticLock.ts).
  const [lockStamp, setLockStamp] = useState<string | null>(member?.updated_at ?? null);
  const [conflict, setConflict] = useState<FamilyMember | null>(null);

  useEffect(() => {
    let alive = true;
    v1ProfileOptions.grouped()
      .then(g => { if (alive) setOpts(g); })
      .catch(() => { /* fällt auf FALLBACK_OPTS zurück */ });
    return () => { alive = false; };
  }, []);

  const set = <K extends keyof PbFormState>(key: K, val: PbFormState[K]) => {
    setF(prev => ({ ...prev, [key]: val }));
    setSavedMsg(null);
  };

  // WICHTIG: profiltyp/familiengrad tragen im Familienkonto das LABEL als
  // `value` (das v2-Backend validiert gegen profile_options.value, nicht die
  // ID) → labelOptions statt fieldOptions. Dadurch enthält der Payload
  // profiltyp:"Frau" / familiengrad:"gesetzlicher Betreuer" statt einer ID.
  const profiltypOptions = useMemo(() => labelOptions('profiltyp', opts, f.profiltyp), [opts, f.profiltyp]);
  const familiengradOptions = useMemo(() => labelOptions('familiengrad', opts), [opts]);

  // is_pflegeempfaenger-Toggle ist die Quelle der Wahrheit für schlanke vs.
  // volle Sektionen. Der Toggle kann durch den Nutzer jederzeit umgestellt
  // werden (auch für neue Personen). isCareRecipientType ist weiterhin für
  // Badge-Rendering in FamilienkontoDetailPage in Verwendung.
  const careRecipient = f.is_pflegeempfaenger ?? false;

  const save = async () => {
    if (saving) return;
    // WP4: full validation replacing the old single-field profiltyp check
    const vr = validateProfile(f);
    if (vr.missing.length > 0) {
      setInvalidFields(vr.missing);
      setError(tr('pflege.familienkonto.detail.missing_fields_error', { fields: missingFieldLabels(vr.missing, tr) }));
      return;
    }
    setInvalidFields([]);
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(f, careRecipient);
      let result: FamilyMember;
      if (member) {
        // Stand nur mitschicken, wenn er bekannt ist — sonst prüft die API nicht
        // (und bestehende Payload-Erwartungen bleiben unverändert).
        result = await v2Members.update(member.profile_id, lockStamp ? { ...payload, updated_at: lockStamp } : payload);
        setSavedMsg(tr('pflege.familienkonto.detail.member_saved'));
      } else {
        if (accountId == null) throw new Error('accountId fehlt');
        result = await v2Members.create(accountId, payload);
        setSavedMsg(tr('pflege.familienkonto.detail.member_saved'));
      }
      setInvalidFields([]);
      setConflict(null);
      setLockStamp(result.updated_at ?? null);
      onSaved(result);
    } catch (e) {
      const stale = toStaleWriteError<FamilyMember>(e);
      if (stale) {
        // Nichts wurde überschrieben. Aktuellen Stand anbieten statt einer
        // generischen Fehlermeldung — der Nutzer entscheidet, was er übernimmt.
        setConflict(stale.current);
        setError(tr('common.conflict.stale_write'));
        return;
      }
      const err = e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } };
      const msg = err.response?.data?.errors?.[0]?.message
        ?? err.response?.data?.message
        ?? (e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  /** Konflikt: aktuellen Server-Stand ins Formular übernehmen (eigene Eingaben verwerfen). */
  const adoptCurrent = () => {
    if (!conflict) return;
    setF(memberToForm(conflict));
    setLockStamp(conflict.updated_at ?? null);
    setConflict(null);
    setError(null);
    setSavedMsg(tr('common.conflict.reloaded'));
  };

  // Pflichtfeld-Markierung der schlanken Sicht: `*` aus validation.ts,
  // roter Rahmen aus dem Validierungsergebnis. Vorher trug hier kein Feld
  // beides — die Meldung „Bitte fülle alle Pflichtfelder aus: vorname,
  // nachname, anrede" nannte also Felder, die im Formular unmarkiert blieben.
  const req = (key: keyof PbFormState) => isRequiredField(f, key);
  const isInvalid = (key: keyof PbFormState) => invalidFields.includes(key);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Kopfblock: Rolle/Familiengrad/Haushalt/Pflegegrad — Optionen mit
          Label-als-value durchreichen (Backend-Vertrag = Label).
          Für Pflegeempfänger wird der Pflegegrad hier ausgeblendet: er steht
          in der Pflege-Sektion (und ist dort das Pflichtfeld). Zwei Selektoren
          mit unterschiedlicher Wertsemantik im selben Formular waren die
          Ursache dafür, dass ein gesetzter Pflegegrad beim Speichern
          verschwand. */}
      <ProfilHeadSection
        f={f}
        set={set}
        opts={opts}
        dark={dark}
        profiltypOptions={profiltypOptions}
        familiengradOptions={familiengradOptions}
        invalidFields={invalidFields}
        showPflegegrad={!careRecipient}
      />

      {careRecipient ? (
        // Volle Pflege-Sektionen (Frau/Mann = Pflegeempfänger).
        <FormSections f={f} set={set} opts={opts} dark={dark} invalidFields={invalidFields} />
      ) : (
        // Schlanke Sicht: Stammdaten + Adresse.
        <>
          <Section dark={dark} id="stammdaten" num="01" title={tr('pflege.form.section.stammdaten.title')}>
            <Grid cols={4}>
              <Field dark={dark} required={req('anrede')} label={tr('pflege.form.field.anrede')} hasError={isInvalid('anrede')}>
                <Select dark={dark} value={f.anrede} onChange={v => set('anrede', v)} placeholder="—" options={['Herr', 'Frau']} hasError={isInvalid('anrede')} />
              </Field>
              <Field dark={dark} label={tr('pflege.form.field.titel')}>
                <Text dark={dark} value={f.titel} onChange={v => set('titel', v)} placeholder={tr('pflege.form.ph.titel')} />
              </Field>
              <Field dark={dark} required={req('vorname')} label={tr('pflege.form.field.vorname')} hasError={isInvalid('vorname')}>
                <Text dark={dark} value={f.vorname} onChange={v => set('vorname', v)} placeholder={tr('pflege.form.ph.vorname')} hasError={isInvalid('vorname')} />
              </Field>
              <Field dark={dark} required={req('nachname')} label={tr('pflege.form.field.nachname')} hasError={isInvalid('nachname')}>
                <Text dark={dark} value={f.nachname} onChange={v => set('nachname', v)} placeholder={tr('pflege.form.ph.nachname')} hasError={isInvalid('nachname')} />
              </Field>
            </Grid>
            <div style={{ marginTop: 16 }}>
              <Grid cols={3}>
                <Field dark={dark} label={tr('pflege.form.field.email')}>
                  <Text dark={dark} value={f.email} onChange={v => set('email', v)} type="email" placeholder="name@beispiel.de" />
                </Field>
                <Field dark={dark} label={tr('pflege.form.field.mobile')}>
                  <PhoneInput dark={dark} value={f.mobile} onChange={v => set('mobile', v)} ariaLabel={tr('pflege.form.field.mobile')} placeholder="170 1234567" />
                </Field>
                <Field dark={dark} label={tr('pflege.form.field.telefon')}>
                  <PhoneInput dark={dark} value={f.telefon} onChange={v => set('telefon', v)} ariaLabel={tr('pflege.form.field.telefon')} placeholder="89 1234567" />
                </Field>
              </Grid>
            </div>
          </Section>

          <Section dark={dark} id="adresse" num="02" title={tr('pflege.form.section.adresse.title')}>
            <Grid cols={6}>
              <Field dark={dark} label={tr('pflege.form.field.strasse')} span={4}><Text dark={dark} value={f.strasse} onChange={v => set('strasse', v)} placeholder={tr('pflege.form.ph.strasse')} /></Field>
              <Field dark={dark} label={tr('pflege.form.field.hausnummer')} span={2}><Text dark={dark} value={f.hausnummer} onChange={v => set('hausnummer', v)} placeholder="12a" /></Field>
              <Field dark={dark} label={tr('pflege.form.field.plz')} span={2}><Text dark={dark} value={f.plz} onChange={v => set('plz', v)} placeholder="80331" /></Field>
              <Field dark={dark} label={tr('pflege.form.field.ort')} span={2}><Text dark={dark} value={f.ort} onChange={v => set('ort', v)} placeholder={tr('pflege.form.ph.ort')} /></Field>
              {/* `land` steht nicht in REQUIRED_CONTACT — in der schlanken
                  Sicht also nie Pflichtfeld; kein `required` nötig. */}
              <Field dark={dark} label={tr('pflege.form.field.land')} span={2} hasError={isInvalid('land')}>
                <Text dark={dark} value={f.land} onChange={v => set('land', v)} placeholder="DE" hasError={isInvalid('land')} />
              </Field>
            </Grid>
          </Section>
        </>
      )}

      {/* Save-Leiste */}
      <Card dark={dark} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div role={error ? 'alert' : undefined} style={{ fontSize: 12.5, color: error ? '#b42318' : t.muted, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>{error ? error : savedMsg ? savedMsg : ''}</span>
          {conflict && (
            <Button variant="secondary" size="sm" dark={dark} onClick={adoptCurrent}>
              {tr('common.conflict.reload')}
            </Button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {onCancel && (
            <Button variant="secondary" size="sm" dark={dark} onClick={onCancel}>
              {tr('pflege.familienkonto.detail.cancel')}
            </Button>
          )}
          <Button variant="primary" size="sm" dark={dark} onClick={save} disabled={saving} icon={<Icon.Check c="#fff" />}>
            {saving ? tr('pflege.edit.saving') : tr('pflege.familienkonto.detail.save_member')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
